/**
 * Magyar ezres tagolású számformázás.
 * Az Intl.NumberFormat 'hu-HU' locale 10 000 alatti számoknál nem tesz szóközt
 * egyes böngészőkben – ezért saját implementáció, ami mindig tagol.
 *
 * @param {number|string} v   - A formázandó szám
 * @param {number}        dec - Tizedes jegyek száma (0 = egész)
 * @returns {string|null}
 */
export function huFmt(v, dec = 0) {
  if (v == null || v === '') return null;
  const n = dec === 0 ? Math.round(Number(v)) : Number(v);
  if (isNaN(n)) return null;
  const [intPart, fracPart] = n.toFixed(dec).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return fracPart !== undefined ? `${grouped},${fracPart}` : grouped;
}

/**
 * Egységes pénzformázó: HUF egész, minden más deviza 2 tizedes.
 * Ez az EGYETLEN hely, ahol összeg+deviza szöveggé alakul – a korábbi
 * komponensenkénti fmtAmt/fmtAmount másolatok helyett.
 *
 * @param {number|string} v        - Az összeg
 * @param {string}        currency - Devizakód (default HUF)
 * @returns {string|null} pl. "38 100 Ft" / "39,00 EUR"
 */
export function fmtMoney(v, currency = 'HUF') {
  const s = huFmt(v, currency === 'HUF' ? 0 : 2);
  if (s == null) return null;
  return currency === 'HUF' ? `${s} Ft` : `${s} ${currency}`;
}

/**
 * Rövidített HUF nagy számokhoz: 1 010 845 693 681 → "1 010,8 Mrd".
 *
 * ⚠ CSAK 1 milliárd fölött rövidít. Egy KKV napi nagyságrendjei (10–100 M)
 * teljes alakban olvashatók, és ott a rövidítés információt venne el; egy
 * 13 jegyű szám viszont kifolyik a KPI-csempéből és olvashatatlan.
 * A hívóhely tegye a PONTOS értéket `title`-be.
 *
 * @param {number} v
 * @returns {string|null}
 */
/**
 * Egységes magyar dátumformázó: ÉÉÉÉ.HH.NN. (`CLAUDE.md` §4)
 *
 * ⚠ 2026-09-16: eddig NEM volt közös dátumformázó a `utils.js`-ben, ezért 8 oldal
 * implementálta helyben (`Documents.js`, `Dashboard.js`, `Partners.js`,
 * `NavReconcile.js` …), a `Statements.js:16` pedig ISO-t hagyott (`2026-08-05`).
 * Ugyanaz a szétcsúszás, ami a `fmtMoney` előtt a `fmtAmt`-másolatokkal volt.
 *
 * @param {string|Date|null} v - ISO dátum vagy Date
 * @returns {string} pl. "2026.08.05." — hiányzó értékre "–"
 */
export function fmtDateHu(v) {
  if (!v) return '–';
  const s = v instanceof Date ? v.toISOString() : String(v);
  const ymd = s.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '–';
  return `${ymd.replace(/-/g, '.')}.`;
}

/**
 * Backend-időbélyeg budapesti idő szerint: „2026.09.26. 19:07” (F0-8, D16).
 *
 * ⚠ A zóna nélküli string UTC-nek értendő (naiv `datetime.utcnow()` oszlopok) — a mobil
 * `huDateTime` (`kit.tsx`) ugyanígy. Levágva (`slice`) 17:07 látszana egy 19:07-es eseményre.
 *
 * @param {string|null} iso
 * @param {{dateOnly?: boolean}} [opts] - `dateOnly`: csak „2026.09.26.” (budapesti nap)
 * @returns {string} hiányzó/érvénytelen értékre "–"
 */
export function fmtDateTimeHu(iso, { dateOnly = false } = {}) {
  if (!iso) return '–';
  const raw = String(iso);
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(raw) ? raw : raw.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return '–';
  const parts = Object.fromEntries(new Intl.DateTimeFormat('hu-HU', {
    timeZone: 'Europe/Budapest', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d).map(p => [p.type, p.value]));
  const day = `${parts.year}.${parts.month}.${parts.day}.`;
  return dateOnly ? day : `${day} ${parts.hour}:${parts.minute}`;
}

const HU_MONTHS = ['január', 'február', 'március', 'április', 'május', 'június', 'július',
  'augusztus', 'szeptember', 'október', 'november', 'december'];

/**
 * Hónap-kulcs („2026-08”) magyarul: „2026. augusztus”, vagy `{short: true}`-val „2026.08.”.
 * @param {string|null} ym
 * @param {{short?: boolean}} [opts]
 */
export function fmtMonthHu(ym, { short = false } = {}) {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(String(ym || ''));
  if (!m) return '–';
  return short ? `${m[1]}.${m[2]}.` : `${m[1]}. ${HU_MONTHS[Number(m[2]) - 1]}`;
}

export function huCompact(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (isNaN(n)) return null;
  const a = Math.abs(n);
  if (a >= 1e12) return `${huFmt(n / 1e12, 1)} Bn`;
  if (a >= 1e9)  return `${huFmt(n / 1e9, 1)} Mrd`;
  return huFmt(n);
}

/**
 * Chart-tengelyekhez: minden nagyságrendet rövidít (ott a hely szűkös).
 * 4 200 000 → "4,2 M" · 820 000 → "820 E" · 950 → "950"
 */
export function huAxis(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e12) return `${huFmt(n / 1e12, 1)} Bn`;
  if (a >= 1e9)  return `${huFmt(n / 1e9, 1)} Mrd`;
  if (a >= 1e6)  return `${huFmt(n / 1e6, 1)} M`;
  if (a >= 1e3)  return `${huFmt(n / 1e3, 0)} E`;
  return huFmt(n);
}

/**
 * Általános IBAN ellenőrző számjegy validálás (ISO 13616, minden ország).
 */
export function isValidIban(iban) {
  const clean = iban?.replace(/\s/g, '').toUpperCase() || '';
  if (clean.length < 5 || !/^[A-Z]{2}\d{2}/.test(clean)) return false;
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  let numStr = '';
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    numStr += (code >= 65 && code <= 90) ? String(code - 55) : ch;
  }
  let rem = 0;
  for (const ch of numStr) rem = (rem * 10 + parseInt(ch)) % 97;
  return rem === 1;
}


/**
 * IBAN MEGJELENÍTÉSI alakja: négyes csoportokban tagolva (ISO 13616 ajánlás,
 * `HU42 1177 3016 1111 1018 0000 0000`) — Gábor kérése, 2026-09-16.
 *
 * ⚠⚠ KIZÁRÓLAG megjelenítésre. A tárolt/összehasonlított/API-ba küldött alak MINDIG
 * a szóköz nélküli, nagybetűs forma marad (`replace(/\s/g,'').toUpperCase()`), mert az
 * `iban_guard`, a dedup és a banki kérések azon illesztenek. Egy tagolt sztring
 * összehasonlítása a tagolatlannal némán „ismeretlen IBAN"-t adna.
 *
 * A magyar 16/24 jegyű belföldi számlaszám (`11773016-11111018-00000000`) NEM IBAN —
 * azt a kötőjeles alakjában hagyjuk, nem tagoljuk át.
 */
export function fmtIban(v) {
  const s = String(v ?? '').replace(/\s/g, '');
  if (!s) return '';
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/i.test(s)) return String(v);   // nem IBAN-alak → érintetlen
  return s.toUpperCase().replace(/(.{4})/g, '$1 ').trim();
}

/** Maszkolt IBAN a megerősítő listákhoz: az első 4 és az utolsó 4 karakter látszik
 * („HU12 …… 3456"). Csak megjelenítés — a teljes, tagolt alak a bizonylat-soron marad
 * (ott vetheti össze a felhasználó a számlával). Nem IBAN-alakú érték → fmtIban. */
export function maskIban(v) {
  const s = String(v ?? '').replace(/\s/g, '').toUpperCase();
  if (!s) return '';
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(s) || s.length < 12) return fmtIban(v);
  return `${s.slice(0, 4)} …… ${s.slice(-4)}`;
}
